import { FC, useMemo } from 'react';
import { Box, Chip, Stack, Avatar, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderStatusLabel } from '@/sections/management/accounts/orders/components/order-status-label';
import { ChoicesOrderStatus } from '@/types';
import { IUserItem } from '@/types/user';

const STATUSES_WITH_RESPONSIBLES: ChoicesOrderStatus[] = [
  ChoicesOrderStatus.pendingApproval,
  ChoicesOrderStatus.pendingProvision,
  ChoicesOrderStatus.provisioning,
];

const StatusHeader: FC<{ status: string; t: (key: string) => string }> = ({ status, t }) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
    <Box
      sx={{
        width: 8,
        height: 8,
        bgcolor: 'primary.main',
        borderRadius: '50%',
      }}
    />
    <Typography variant="subtitle2">{t(status)}</Typography>
  </Stack>
);

const ResponsiblesList: FC<{ users: IUserItem[]; title: string }> = ({ users, title }) => (
  <Stack spacing={1}>
    <Typography variant="body2" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    {users.map((user) => (
      <Stack key={user._id} direction="row" spacing={1} alignItems="center">
        <Avatar
          src={user.avatar?.url ? decodeURI(user.avatar.url) : undefined}
          alt={`${user.firstName} ${user.lastName}`}
          sx={{ width: 28, height: 28 }}
        />
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
      </Stack>
    ))}
  </Stack>
);

export interface OrderStatusWithTooltipProps {
  status: ChoicesOrderStatus;
  approvalResponsibles?: IUserItem[];
  provisionResponsibles?: IUserItem[];
  hasSyncError?: boolean | number;
  className?: string;
}

export const OrderStatusWithTooltip: FC<OrderStatusWithTooltipProps> = ({
  status,
  approvalResponsibles = [],
  provisionResponsibles = [],
  hasSyncError = false,
  className,
}) => {
  const { t } = useTranslation();

  const responsibles = useMemo(() => {
    switch (status) {
      case ChoicesOrderStatus.pendingApproval:
        return approvalResponsibles;
      case ChoicesOrderStatus.pendingProvision:
      case ChoicesOrderStatus.provisioning:
        return provisionResponsibles;
      default:
        return [];
    }
  }, [status, approvalResponsibles, provisionResponsibles]);

  const statusConfig = useMemo(() => {
    const hasResponsibles = STATUSES_WITH_RESPONSIBLES.includes(status);
    const isCreated = status === ChoicesOrderStatus.created;

    const messages: Record<string, string> = {
      [ChoicesOrderStatus.created]: t(
        'The order has not been sent yet, so the approval flow has not started.'
      ),
      [ChoicesOrderStatus.completed]: t('Order completed; there are no active responsibles.'),
    };

    // Handle rejected/cancelled/failed statuses
    if (
      [
        ChoicesOrderStatus.cancelled,
        ChoicesOrderStatus.rejected,
        ChoicesOrderStatus.failedProvision,
      ].includes(status)
    ) {
      messages[status] = t('The request was {{status}}; no active responsibles.', {
        status: t(status),
      });
    }

    const getResponsibleTitle = (orderStatus: ChoicesOrderStatus): string => {
      switch (orderStatus) {
        case ChoicesOrderStatus.pendingApproval:
          return t('Approval responsibles:');
        case ChoicesOrderStatus.pendingProvision:
          return t('Provision responsibles:');
        case ChoicesOrderStatus.provisioning:
          return t('Provision responsibles:');
        default:
          return t('Responsibles:');
      }
    };

    return {
      hasResponsibles,
      isCreated,
      message: messages[status] || t('Unknown status'),
      title: getResponsibleTitle(status),
    };
  }, [status, t]);

  const tooltipContent = useMemo(() => {
    const hasResponsibles = responsibles.length > 0 && statusConfig.hasResponsibles;
    const showResponsiblesNotAvailable = statusConfig.hasResponsibles && !hasResponsibles;

    return (
      <Box>
        {hasResponsibles ? (
          <ResponsiblesList users={responsibles} title={statusConfig.title} />
        ) : statusConfig.isCreated ? (
          <Typography variant="body2">{statusConfig.message}</Typography>
        ) : (
          <>
            <StatusHeader status={status} t={t} />
            <Typography variant="body2">
              {showResponsiblesNotAvailable
                ? t('Responsibles: Not available')
                : statusConfig.message}
            </Typography>
          </>
        )}
      </Box>
    );
  }, [status, responsibles, statusConfig, t]);

  const statusBadge = (
    <Stack direction="column" alignItems="center" spacing={0.5}>
      <OrderStatusLabel status={status} />
      {Boolean(hasSyncError) && (
        <Chip
          size="small"
          label={t('Sync Error')}
          color="error"
          variant="outlined"
          sx={{ fontSize: '0.75rem', height: '20px' }}
        />
      )}
    </Stack>
  );

  const tooltipProps = {
    title: tooltipContent,
    placement: 'top' as const,
    arrow: true,
    enterDelay: 300,
    leaveDelay: 200,
    slotProps: {
      tooltip: {
        sx: {
          backgroundColor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 4,
          borderRadius: 1,
          maxWidth: 350,
          p: 1.5,
        },
      },
      arrow: {
        sx: {
          color: 'background.paper',
          '&:before': {
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      },
    },
  };

  return (
    <Tooltip {...tooltipProps}>
      <Box
        className={className}
        sx={{
          cursor: 'help',
          display: 'inline-block',
        }}
      >
        {statusBadge}
      </Box>
    </Tooltip>
  );
};

export default OrderStatusWithTooltip;
